import type {
  EditableQuestion,
  QuestionSectionKind,
  RawComponent,
  RawQuestion,
  RawQuestionSection,
  RawSection,
  RawUpdateCoursePayload,
  UpdateCourseSavePayload,
} from '@/features/content/editor/editorTypes';

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

export function cloneValue<T>(value: T): T {
  if (typeof structuredClone === 'function') {
    return structuredClone(value);
  }

  return JSON.parse(JSON.stringify(value)) as T;
}

export function toSnakeCaseKey(value: string) {
  return value.replace(/[A-Z]/g, (match) => `_${match.toLowerCase()}`);
}

export function toSnakeCaseValue<T>(value: T): T {
  if (Array.isArray(value)) {
    return value.map((entry) => toSnakeCaseValue(entry)) as T;
  }

  if (!isRecord(value)) {
    return value;
  }

  return Object.entries(value).reduce<Record<string, unknown>>((result, [key, entry]) => {
    result[toSnakeCaseKey(key)] = toSnakeCaseValue(entry);
    return result;
  }, {}) as T;
}

export function normalizeIdentifier(value: unknown): string | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }

  if (typeof value === 'bigint') {
    return value.toString();
  }

  if (typeof value !== 'string') {
    return null;
  }

  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
}

export function toSectionArray(value: unknown): RawSection[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter(isRecord) as RawSection[];
}

export function getSectionId(section: RawSection, index: number) {
  return (
    normalizeIdentifier(section.id) ??
    normalizeIdentifier(section.uid) ??
    `__generated-section-${index + 1}`
  );
}

export function getQuestionSection(
  section: RawSection,
  scope: QuestionSectionKind,
): RawQuestionSection | null {
  const rawSection =
    scope === 'homework'
      ? section.question_section ?? section.questionSection
      : section.pre_lecture_question_section ?? section.preLectureQuestionSection;

  return isRecord(rawSection) ? (rawSection as RawQuestionSection) : null;
}

export function ensureQuestionSection(
  sections: RawSection[],
  scope: QuestionSectionKind,
) {
  const existing = sections
    .map((section) => getQuestionSection(section, scope))
    .find((questionSection): questionSection is RawQuestionSection =>
      Boolean(questionSection),
    );

  if (existing) {
    if (!Array.isArray(existing.questions)) {
      existing.questions = [];
    }

    return existing;
  }

  const [firstSection] = sections;

  if (!firstSection) {
    throw new Error('Cannot update questions because the component has no sections.');
  }

  const firstSectionId = getSectionId(firstSection, 0);
  const questionSection: RawQuestionSection = {
    id:
      scope === 'homework'
        ? `${firstSectionId}-question-section`
        : `${firstSectionId}-pre-lecture-question-section`,
    questions: [],
  };

  if (scope === 'homework') {
    firstSection.question_section = questionSection;
    delete (firstSection as RawSection & { questionSection?: unknown })
      .questionSection;
  } else {
    firstSection.pre_lecture_question_section = questionSection;
    delete (
      firstSection as RawSection & { preLectureQuestionSection?: unknown }
    ).preLectureQuestionSection;
  }

  return questionSection;
}

export function toRawQuestions(value: unknown): RawQuestion[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter(isRecord) as RawQuestion[];
}

function getQuestionTypeEnum(question: EditableQuestion) {
  if (question.type === 'AI_UPLOAD') {
    return 6;
  }

  if (question.type === 'NUMERICAL_WITH_ERROR') {
    return 3;
  }

  if (question.type === 'EXACT_ANSWER') {
    return 4;
  }

  if (question.type === 'MULTI_PART') {
    return 5;
  }

  return 1;
}

export function editableQuestionToRawQuestion(question: EditableQuestion) {
  if (question.rawOriginal) {
    const raw = toSnakeCaseValue(cloneValue(question.rawOriginal)) as RawQuestion;
    // A copy is a new question in another chapter: it does not inherit a
    // removal from the assignment it came from.
    delete raw.removed;
    return raw;
  }

  return {
    content: question.contentHtml,
    id: question.id,
    type: getQuestionTypeEnum(question),
    value: question.value,
  } satisfies RawQuestion;
}

export function createUpdateCoursePayload(
  courseId: string,
  component: RawComponent,
): UpdateCourseSavePayload {
  const sections = toSectionArray(component.sections);

  return {
    components: [component],
    id: courseId,
    sections,
  };
}

export function unwrapSavedComponent(
  response:
    | RawUpdateCoursePayload
    | { Response?: RawUpdateCoursePayload | RawComponent | null }
    | RawComponent
    | null
    | undefined,
  fallbackComponent: RawComponent,
): RawComponent {
  if (response && typeof response === 'object' && 'Response' in response) {
    return unwrapSavedComponent(response.Response, fallbackComponent);
  }

  if (
    response &&
    typeof response === 'object' &&
    'components' in response &&
    Array.isArray(response.components)
  ) {
    const [firstComponent] = response.components;
    return (firstComponent as RawComponent | undefined) ?? fallbackComponent;
  }

  if (response && typeof response === 'object' && 'component' in response) {
    return (response.component as RawComponent | null | undefined) ?? fallbackComponent;
  }

  if (
    response &&
    typeof response === 'object' &&
    ('sections' in response || 'id' in response || 'uid' in response)
  ) {
    return response as RawComponent;
  }

  return fallbackComponent;
}

